import React, {useEffect, useState, useRef} from "react";
import Navbar from "../Navbar/Navbar";
import SideMenu from "./SideMenu";
import { toast } from "../ui/use-toast";
import { OrderModel } from "@/api/Models";
import { OrdersListResponse } from "@/api/OrdersAPI";
import ApiHttpClient from "@/HttpClient";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {getDecodedTokenDetails} from "@/components/Token/TokenService";

const SearchInput: React.FC<any> = ({searchOrder, setSearchOrder, setIsNewSearch, searchInputText}) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        
        if (value.length > 1) {
            setSearchOrder(value);
            setIsNewSearch(true);
        } else {
            setSearchOrder(value);
        }
    };
    
    return (
        <input
            className="block w-full rounded-lg border border-gray-200 p-3 text-gray-600 hover:border-black"
            type="search"
            value={searchOrder}
            onChange={handleChange}
            placeholder={searchInputText}
        />
    );
};

function MyOrdersInfo() {
    const [currentPage, setCurrentPage] = useState(0);
    const [searchOrder, setSearchOrder] = useState('');
    const [isNewSearch, setIsNewSearch] = useState(false);
    const [isLoadingSearch, setIsLoadingSearch] = useState(false);
    const [data, setData] = useState<OrdersListResponse>();
    const [dateMin, setDateMin] = useState<any>();
    const [dateMax, setDateMax] = useState<any>(new Date());
    const [userRole, setUserRole] = useState("");
    const [searchInputText, setSearchInputText] = useState("");
    const [inputValue, setInputValue] = useState(currentPage + 1);
    const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);
    const pageSize = 30;

    const detailsRef = useRef<HTMLDetailsElement>(null);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (
                detailsRef.current &&
                !detailsRef.current.contains(event.target as Node)
            ) {
                detailsRef.current.removeAttribute("open");
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        (async () => {
            const tokenDetails = await getDecodedTokenDetails();

            if (tokenDetails?.token) {
                try {
                    const userRole = tokenDetails.userRole
                    setUserRole(userRole);
                    
                    if(userRole !== "BASIC")
                        setSearchInputText("Cauta codul unei comenzi, precomenzi sau numele unei organizatii...")
                    else setSearchInputText("Caută codul unei comenzi sau al unei precomenzi...")
                } catch (error) {
                    console.error('Failed to decode token', error);
                }
            } else {
                console.error('No token found');
            }
        })();
    }, []);

    useEffect(() => {
        setInputValue(currentPage + 1);
    }, [currentPage]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = Number(e.target.value);

        if (data?.totalPages && value > data.totalPages) {
            value = data.totalPages;
        } else if (value < 1) {
            value = 1;
        }
        setInputValue(value);

        if (debounceTimer) clearTimeout(debounceTimer);

        const timer = setTimeout(() => {
            if (data?.totalPages && value >= 1 && value <= data?.totalPages) {
                handlePageChange(value - 1);
            }
        }, 500);

        setDebounceTimer(timer);
    };
    
    const handlePageChange = (selectedPage: any) => {
        setCurrentPage(selectedPage);
        setIsNewSearch(false);
    };
    
    const handleRemoveFilters = () => {
        setSearchOrder('');
        setIsNewSearch(false);
        setDateMin('');
        setDateMax(new Date());
    }

    useEffect(() => {
        const updateOrders = async () => {
            setIsLoadingSearch(true);
            
            let response;
            const filters = [];
            
            if (dateMin) {
                filters.push({
                    field: "orderDate",
                    operator: "gte",
                    value: new Date(dateMin).toISOString()
                });
            }
            
            if(dateMax) {
                filters.push({
                    field: "orderDate",
                    operator: "lte",
                    value: new Date(dateMax).toISOString()
                });
            }
            
            const requestBody = {
                advancedFilter: {
                    logic: "and",
                    filters: filters
                },
                orderBy: ["id Desc"],
                pageNumber: currentPage + 1,
                pageSize: pageSize
            }
            
            const requestBodySearchAndDate = {
                advancedFilter: {
                    logic: "and",
                    filters: filters
                }, 
                advancedSearch: {
                    fields: [
                        "preOrderId",
                        "workingPoint.organization.name",
                        "id"
                    ],
                    keyword: searchOrder
                },
                orderBy: ["id Desc"],
                pageNumber: currentPage + 1,
                pageSize: pageSize,
            }
            
            if (searchOrder) {
                if (isNewSearch) setCurrentPage(0);

                if (dateMin && dateMax) {
                    response = await  ApiHttpClient().post<OrdersListResponse>(`/order/search`, requestBodySearchAndDate);
                } else {
                    response = await ApiHttpClient().post<OrdersListResponse>('/order/search', {
                        "advancedSearch": {
                            "fields": [
                                "preOrderId",
                                "workingPoint.organization.name",
                                "id"
                            ],
                            "keyword": searchOrder
                        },
                        "orderBy": ["id Desc"],
                        "pageNumber": currentPage + 1,
                        "pageSize": pageSize,
                    });
                }
                
            } else if (dateMin && dateMax) {
                response = await  ApiHttpClient().post<OrdersListResponse>(`/order/search`, requestBody);
            } else {
                response = await ApiHttpClient().post<OrdersListResponse>(`/order/search`, {
                    "keyword": "",
                    "pageNumber": currentPage + 1,
                    "pageSize": pageSize,
                    "orderBy": ["id Desc"],
                });
            }
            if (!response?.data) {
                toast({description: "A aparut o eroare pentru afisarea comenzilor"});
                return <></>;
            }
            setIsLoadingSearch(false);
            setData(response.data);
        }
        
        updateOrders();
    }, [searchOrder, currentPage, pageSize, isNewSearch, dateMin, dateMax])

    return (
        <>
            <Navbar />
            <div className="flex flex-row items-start">
                <SideMenu />
                {/* Desktop Version */}
                <div className="hidden mx-auto md:block z-auto">
                    {!isLoadingSearch && data?.totalPages === 0 && <p className="mt-5 ml-5 mb-3">Nu au fost gasite rezultate.</p>}
                    <div className="relative overflow-x-auto shadow-md sm:rounded-lg overflow-y-auto pt-4 pb-16 mx-8">

                        <div className="flex justify-end mb-2 mr-2">
                            <button
                                className="inline-flex items-center justify-center rounded-full bg-gray-100 px-2.5 py-0.5 text-gray-700"
                                onClick={handleRemoveFilters}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke-width="1.5"
                                    stroke="currentColor"
                                    className="size-3"
                                >
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                                </svg>
    
                                <p className="text-sm whitespace-nowrap ml-2">Elimina filtrele</p>
                            </button>
                        </div>
                        
                        <div className="mb-5">
                            <SearchInput searchOrder={searchOrder} setSearchOrder={setSearchOrder} setIsNewSearch={setIsNewSearch} searchInputText={searchInputText} />
                        </div>

                        <details ref={detailsRef} className="group relative mb-5">
                            <summary
                                className="mx-2 flex items-center gap-2 pb-1 text-gray-700 transition-colors [&::-webkit-details-marker]:hidden"
                            >
                                <span className="text-sm font-medium"> Cauta comenzile din </span>

                                <span className="transition-transform group-open:-rotate-180">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        strokeWidth="1.5"
                                        stroke="currentColor"
                                        className="size-4"
                                    >
                                      <path strokeLinecap="round" strokeLinejoin="round"
                                            d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
                                    </svg>
                                  </span>
                            </summary>

                            <div
                                className="z-100 w-64 divide-y divide-gray-300 rounded border border-gray-300 bg-white shadow-sm group-open:absolute group-open:start-0 group-open:top-8"
                            >
                                <div className="flex items-center justify-between px-3 py-2">
                                    <span className="text-sm text-gray-700"> Selecteaza datele </span>

                                    <button
                                        type="button"
                                        onClick={() => {
                                            setDateMin('');
                                            setDateMax(new Date())
                                        }}
                                        className="text-sm text-gray-700 underline transition-colors hover:text-gray-900"
                                    >
                                        Reset
                                    </button>
                                </div>

                                <div className="flex items-center gap-3 p-3">
                                    <label htmlFor="MinPrice">
                                        <span className="text-sm text-gray-700"> Data de inceput </span>

                                        <DatePicker
                                            selected={dateMin}
                                            onChange={(dateMin) => setDateMin(dateMin)}
                                            dateFormat="dd/MM/yyyy"
                                            className="mt-0.5 w-full rounded border-gray-300 shadow-sm sm:text-sm z-100"
                                            popperPlacement="bottom-start"
                                            popperModifiers={[
                                                {
                                                    name: "preventOverflow",
                                                    options: {boundary: "viewport", tether: false},
                                                    fn: () => ({}),
                                                },
                                                {
                                                    name: "flip",
                                                    options: {fallbackPlacements: []}, // Prevents flipping above
                                                    fn: () => ({}),
                                                },
                                            ]}
                                            portalId="root-portal" // Ensures calendar is rendered outside restrictive containers
                                        />
                                    </label>

                                    <label htmlFor="MaxPrice">
                                        <span className="text-sm text-gray-700"> Data de sfarsit </span>

                                        <DatePicker
                                            selected={dateMax}
                                            onChange={(dateMax) => setDateMax(dateMax)}
                                            dateFormat="dd/MM/yyyy"
                                            className="mt-0.5 w-full rounded border-gray-300 shadow-sm sm:text-sm z-100"
                                            popperPlacement="bottom-start"
                                            popperModifiers={[
                                                {
                                                    name: "preventOverflow",
                                                    options: {boundary: "viewport", tether: false},
                                                    fn: () => ({}),
                                                },
                                                {
                                                    name: "flip",
                                                    options: {fallbackPlacements: []},
                                                    fn: () => ({}),
                                                },
                                            ]}
                                            portalId="root-portal"
                                        />
                                    </label>
                                </div>
                            </div>
                        </details>

                        <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
                            <thead
                                className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                            <tr>
                                <th scope="col" className="px-6 py-3">
                                    Numarul comenzii
                                </th>
                                <th scope="col" className="px-6 py-3">
                                    Numarul precomenzii
                                </th>
                                {(userRole !== "BASIC") && <th scope="col" className="px-6 py-3">
                                    Numele organizatiei
                                </th>}
                                <th scope="col" className="px-6 py-3">
                                    Data plasarii comenzii
                                </th>
                                <th scope="col" className="px-6 py-3">
                                    Data de livrare estimata
                                </th>
                                <th scope="col" className="px-6 py-3">
                                    Data reala de livrare
                                </th>
                                <th scope="col" className="px-6 py-3">
                                    Status comanda
                                </th>
                                <th scope="col" className="px-6 py-3">
                                    Valoare comanda
                                </th>
                            </tr>
                            </thead>
                            <tbody>

                            {data?.items.map((order: OrderModel) => {
                                return (
                                    <tr className="odd:bg-white odd:dark:bg-gray-900 even:bg-gray-50 even:dark:bg-gray-800 border-b dark:border-gray-700">

                                        <td className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white">
                                            <a href={"/my-account/orders/order-detail/" + order.id}
                                               className="text-blue-500 underline z-auto">{order.id}</a>
                                        </td>
                                       <td
                                            className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white">
                                            <a href={"/my-account/preorders/pre-order-detail/" + order.preOrderId}
                                               className="text-blue-500 underline z-auto">{order.preOrderId}</a>
                                        </td>
                                        {(userRole !== "BASIC") && <td className="px-6 py-4">
                                            {order.workingPoint.organization.name}
                                        </td>}
                                        <td className="px-6 py-4">
                                            {order.orderDate.split("T")[0]}
                                        </td>
                                        <td className="px-6 py-4">
                                            {order.expectedDeliveryDate.split("T")[0]}
                                        </td>
                                        <td className="px-6 py-4">
                                            {order.shippings ? order.shippings[0]?.deliveryDate?.split("T")[0] : '-'}
                                        </td>
                                        <td className="px-6 py-4">
                                            {order.documentStatus.documentStatusName}
                                        </td>
                                        <td className="px-6 py-4">
                                            {order.totalValue} {order.currency}
                                        </td>

                                    </tr>
                                );
                            })}

                            </tbody>
                        </table>
                    </div>

                    <div className="text-center mt-8 mb-2">
                        <ul className="flex justify-center gap-1 text-gray-900 mt-3">
                            <li>
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (currentPage > 0) handlePageChange(currentPage - 1);
                                    }}
                                    className="grid mr-2 size-8 place-content-center rounded border border-gray-200 transition-colors hover:bg-gray-50 rtl:rotate-180"
                                    aria-label="Previous page"
                                    disabled={currentPage === 0}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="size-4"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                </button>
                            </li>

                            <li>
                                <label htmlFor="Page" className="flex items-center gap-1">
                                    <span className="sr-only">Pagina</span>
                                    <input
                                        type="number"
                                        id="Page"
                                        value={inputValue}
                                        min={1}
                                        max={data?.totalPages}
                                        onChange={handleInputChange}
                                        className="h-8 w-10 rounded border-gray-300 text-center sm:text-sm"
                                    />
                                    <span className="text-sm">/ {data?.totalPages}</span>
                                </label>
                            </li>

                            <li>
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (data && currentPage < data.totalPages - 1) handlePageChange(currentPage + 1);
                                    }}
                                    className="grid ml-2 size-8 place-content-center rounded border border-gray-200 transition-colors hover:bg-gray-50 rtl:rotate-180"
                                    aria-label="Next page"
                                    disabled={data && currentPage === data.totalPages - 1}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="size-4"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Mobile Version */}
                <div className="mx-auto max-w-screen-xl md:hidden z-0">
                    {!isLoadingSearch && data?.totalPages === 0 && <p>Nu au fost gasite rezultate.</p>}

                    <div className="flex justify-end mt-4 mr-2">
                        <button
                            className="inline-flex items-center justify-center rounded-full bg-gray-100 px-2.5 py-0.5 text-gray-700"
                            onClick={handleRemoveFilters}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke-width="1.5"
                                stroke="currentColor"
                                className="size-3"
                            >
                                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                            </svg>

                            <p className="text-sm whitespace-nowrap ml-2">Elimina filtrele</p>
                        </button>
                    </div>

                    <div className="-my-3 py-3 ml-12 mt-2 mb-8 mr-12">
                        <SearchInput searchOrder={searchOrder} setSearchOrder={setSearchOrder}
                                     setIsNewSearch={setIsNewSearch} searchInputText={searchInputText}/>
                    </div>

                    <details className="group relative ml-12">
                        <summary
                            className="flex items-center gap-2 pb-1 text-gray-700 transition-colors [&::-webkit-details-marker]:hidden"
                        >
                            <span className="text-sm font-medium"> Cauta comenzile din </span>

                            <span className="transition-transform group-open:-rotate-180">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        strokeWidth="1.5"
                                        stroke="currentColor"
                                        className="size-4"
                                    >
                                      <path strokeLinecap="round" strokeLinejoin="round"
                                            d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
                                    </svg>
                                  </span>
                        </summary>

                        <div
                            className="z-100 w-64 divide-y divide-gray-300 rounded border border-gray-300 bg-white shadow-sm group-open:absolute group-open:start-0 group-open:top-8"
                        >
                            <div className="flex items-center justify-between px-3 py-2">
                                <span className="text-sm text-gray-700"> Selecteaza datele </span>

                                <button
                                    type="button"
                                    onClick={() => {
                                        setDateMin('');
                                        setDateMax(new Date())
                                    }}
                                    className="text-sm text-gray-700 underline transition-colors hover:text-gray-900"
                                >
                                    Reset
                                </button>
                            </div>

                            <div className="flex items-center gap-3 p-3">
                                <label htmlFor="MinPrice">
                                    <span className="text-sm text-gray-700"> Data de inceput </span>

                                    <DatePicker
                                        selected={dateMin}
                                        onChange={(dateMin) => setDateMin(dateMin)}
                                        dateFormat="dd/MM/yyyy"
                                        className="mt-0.5 w-full rounded border-gray-300 shadow-sm sm:text-sm z-100"
                                        popperPlacement="bottom-start"
                                        popperModifiers={[
                                            {
                                                name: "preventOverflow",
                                                options: {boundary: "viewport", tether: false},
                                                fn: () => ({}),
                                            },
                                            {
                                                name: "flip",
                                                options: {fallbackPlacements: []}, // Prevents flipping above
                                                fn: () => ({}),
                                            },
                                        ]}
                                        portalId="root-portal" // Ensures calendar is rendered outside restrictive containers
                                    />
                                </label>

                                <label htmlFor="MaxPrice">
                                    <span className="text-sm text-gray-700"> Data de sfarsit </span>

                                    <DatePicker
                                        selected={dateMax}
                                        onChange={(dateMax) => setDateMax(dateMax)}
                                        dateFormat="dd/MM/yyyy"
                                        className="mt-0.5 w-full rounded border-gray-300 shadow-sm sm:text-sm z-100"
                                        popperPlacement="bottom-start"
                                        popperModifiers={[
                                            {
                                                name: "preventOverflow",
                                                options: {boundary: "viewport", tether: false},
                                                fn: () => ({}),
                                            },
                                            {
                                                name: "flip",
                                                options: {fallbackPlacements: []},
                                                fn: () => ({}),
                                            },
                                        ]}
                                        portalId="root-portal"
                                    />
                                </label>
                            </div>
                        </div>
                    </details>

                    {data?.items.map((order: OrderModel) => {
                        return (
                            <div
                                className="flow-root rounded-lg border border-gray-100 py-3 ml-12 mt-8 mb-8 mr-12 shadow-sm">
                                <dl className="-my-3 divide-y divide-gray-100 text-sm">
                                    <div className="grid grid-cols-1 gap-1 p-3 even:bg-gray-50 sm:grid-cols-3 sm:gap-4">
                                        <dt className="font-medium text-gray-900">Numarul comenzii</dt>
                                        <dd className="text-gray-700 sm:col-span-2"><a
                                            href={"/my-account/orders/order-detail/" + order.id}
                                            className="text-blue-500 underline">{order.id}</a></dd>
                                    </div>

                                   <div
                                        className="grid grid-cols-1 gap-1 p-3 even:bg-gray-50 sm:grid-cols-3 sm:gap-4">
                                        <dt className="font-medium text-gray-900">Numarul precomenzii</dt>
                                        <dd className="text-gray-700 sm:col-span-2"><a
                                            href={"/my-account/preorders/pre-order-detail/" + order.preOrderId}
                                            className="text-blue-500 underline">{order.preOrderId}</a></dd>
                                    </div>

                                    {(userRole !== "BASIC") && <div
                                        className="grid grid-cols-1 gap-1 p-3 even:bg-gray-50 sm:grid-cols-3 sm:gap-4">
                                        <dt className="font-medium text-gray-900">Numele organizatiei</dt>
                                        <dd className="text-gray-700 sm:col-span-2">{order.workingPoint.organization.name}</dd>
                                    </div>}

                                    <div className="grid grid-cols-1 gap-1 p-3 even:bg-gray-50 sm:grid-cols-3 sm:gap-4">
                                        <dt className="font-medium text-gray-900">Data plasarii comenzii</dt>
                                        <dd className="text-gray-700 sm:col-span-2">{order.orderDate.split("T")[0]}</dd>
                                    </div>

                                    <div className="grid grid-cols-1 gap-1 p-3 even:bg-gray-50 sm:grid-cols-3 sm:gap-4">
                                        <dt className="font-medium text-gray-900">Data de livrare estimata</dt>
                                        <dd className="text-gray-700 sm:col-span-2">{order.expectedDeliveryDate.split("T")[0]}</dd>
                                    </div>

                                    <div className="grid grid-cols-1 gap-1 p-3 even:bg-gray-50 sm:grid-cols-3 sm:gap-4">
                                        <dt className="font-medium text-gray-900">Data reala de livrare</dt>
                                        <dd className="text-gray-700 sm:col-span-2">{order.shippings[0]?.deliveryDate ? order.shippings[0]?.deliveryDate?.split("T")[0] : '-'}</dd>
                                    </div>

                                    <div className="grid grid-cols-1 gap-1 p-3 even:bg-gray-50 sm:grid-cols-3 sm:gap-4">
                                        <dt className="font-medium text-gray-900">Status comanda</dt>
                                        <dd className="text-gray-700 sm:col-span-2">{order.documentStatus.documentStatusName}</dd>
                                    </div>

                                    <div className="grid grid-cols-1 gap-1 p-3 even:bg-gray-50 sm:grid-cols-3 sm:gap-4">
                                        <dt className="font-medium text-gray-900">Valoare comanda</dt>
                                        <dd className="text-gray-700 sm:col-span-2">{order.totalValue} {order.currency}</dd>
                                    </div>
                                </dl>
                            </div>
                        );
                    })}

                    <div className="text-center mb-4">
                        <ul className="flex justify-center gap-1 text-gray-900 mt-3">
                            <li>
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (currentPage > 0) handlePageChange(currentPage - 1);
                                    }}
                                    className="grid mr-2 size-8 place-content-center rounded border border-gray-200 transition-colors hover:bg-gray-50 rtl:rotate-180"
                                    aria-label="Previous page"
                                    disabled={currentPage === 0}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="size-4"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                </button>
                            </li>

                            <li>
                                <label htmlFor="Page" className="flex items-center gap-1">
                                    <span className="sr-only">Pagina</span>
                                    <input
                                        type="number"
                                        id="Page"
                                        value={inputValue}
                                        min={1}
                                        max={data?.totalPages}
                                        onChange={handleInputChange}
                                        className="h-8 w-10 rounded border-gray-300 text-center sm:text-sm"
                                    />
                                    <span className="text-sm">/ {data?.totalPages}</span>
                                </label>
                            </li>

                            <li>
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (data && currentPage < data.totalPages - 1) handlePageChange(currentPage + 1);
                                    }}
                                    className="grid ml-2 size-8 place-content-center rounded border border-gray-200 transition-colors hover:bg-gray-50 rtl:rotate-180"
                                    aria-label="Next page"
                                    disabled={data && currentPage === data.totalPages - 1}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="size-4"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </>
    );
}

export default MyOrdersInfo;